import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  InputNumber,
  DatePicker,
  Tooltip,
  Row,
  Col,
  Statistic,
  Tabs,
  Badge,
  Avatar,
  Timeline,
  Alert,
  App,
} from 'antd';
import {
  PlusOutlined,
  WalletOutlined,
  TransactionOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  UserOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const WalletInformation = () => {
  const { message } = App.useApp();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTransactionModalVisible, setIsTransactionModalVisible] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [form] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [walletSearchTerm, setWalletSearchTerm] = useState('');
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState('wallets');
  const queryClient = useQueryClient();

  // Fetch recent commission changes
  const {
    data: commissionChanges = [],
    isLoading: commissionChangesLoading,
    refetch: refetchCommissionChanges,
  } = useQuery(
    'recent-commission-changes',
    async () => {
      try {
        const response = await api.get('/admin/recent-commission-changes');
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch commission changes:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 15000, // Refresh every 15 seconds for real-time updates
      staleTime: 1 * 60 * 1000, // 1 minute
      onSuccess: (data) => {
        if (data.length > 0) {
          // Show notification for new commission changes
          const latestChange = data[0];
          const timeDiff = dayjs().diff(dayjs(latestChange.timestamp), 'minutes');
          if (timeDiff <= 5) {
            // Only notify for very recent changes
            message.success({
              content: (
                <div>
                  <div style={{ fontWeight: '600' }}>New Commission Activity!</div>
                  <div>
                    {latestChange.user_name} - ₨{latestChange.amount?.toFixed(2)}
                  </div>
                </div>
              ),
              duration: 4,
              icon: <TrophyOutlined style={{ color: '#52c41a' }} />,
            });
          }
        }
      },
    },
  );

  // Fetch wallets with filters
  const { data: wallets = [], isLoading: walletsLoading } = useQuery(
    ['wallets', walletSearchTerm, statusFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (walletSearchTerm) params.append('search', walletSearchTerm);
        if (statusFilter) params.append('status', statusFilter);

        const response = await api.get(`/admin/wallets?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch wallets:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Fetch transactions with filters
  const { data: transactions = [], isLoading: transactionsLoading } = useQuery(
    ['transactions', transactionSearchTerm, transactionTypeFilter, dateRange],
    async () => {
      try {
        const params = new URLSearchParams();
        if (transactionSearchTerm) params.append('search', transactionSearchTerm);
        if (transactionTypeFilter) params.append('type', transactionTypeFilter);
        if (dateRange && dateRange.length === 2) {
          params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
          params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
        }

        const response = await api.get(`/admin/wallet-transactions?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 15000,
      staleTime: 2 * 60 * 1000,
    },
  );

  // Add balance mutation
  const addBalanceMutation = useMutation(
    async ({ id, amount, description }) => {
      const response = await api.post(`/admin/wallets/${id}/add-balance`, {
        amount,
        description,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Balance added successfully');
        setIsModalVisible(false);
        setSelectedWallet(null);
        form.resetFields();
        queryClient.invalidateQueries('wallets');
        queryClient.invalidateQueries('transactions');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to add balance');
      },
    },
  );

  // Manual transaction mutation
  const manualTransactionMutation = useMutation(
    async (transactionData) => {
      const response = await api.post('/admin/wallet-transactions', transactionData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Transaction created successfully');
        setIsTransactionModalVisible(false);
        transactionForm.resetFields();
        queryClient.invalidateQueries('transactions');
        queryClient.invalidateQueries('wallets');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to create transaction');
      },
    },
  );

  const handleAddBalance = (wallet) => {
    setSelectedWallet(wallet);
    form.setFieldsValue({
      user_id: wallet.user_id,
      current_balance: wallet.balance,
    });
    setIsModalVisible(true);
  };

  const handleManualTransaction = () => {
    setIsTransactionModalVisible(true);
  };

  const handleSubmitAddBalance = async (values) => {
    try {
      addBalanceMutation.mutate({
        id: selectedWallet.id,
        amount: values.amount,
        description: values.description,
      });
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleSubmitTransaction = async (values) => {
    try {
      manualTransactionMutation.mutate(values);
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const walletColumns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            src={user.avatar}
            icon={<UserOutlined />}
            size="small"
            style={{ marginRight: 8 }}
          />
          <div>
            <div style={{ fontWeight: '500' }}>{user.name}</div>
            <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'User Type',
      dataIndex: ['user', 'user_type'],
      key: 'user_type',
      render: (userType) => (
        <Tag
          color={
            userType === 'admin'
              ? 'red'
              : userType === 'provider'
                ? 'blue'
                : userType === 'delivery'
                  ? 'green'
                  : 'orange'
          }
        >
          {userType?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Current Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => (
        <span
          style={{
            fontWeight: '600',
            color: balance >= 0 ? '#52c41a' : '#ff4d4f',
          }}
        >
          ₨{balance?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Total Earned',
      dataIndex: 'total_earned',
      key: 'total_earned',
      render: (earned) => <span style={{ color: '#52c41a' }}>+₨{earned?.toFixed(2)}</span>,
    },
    {
      title: 'Total Spent',
      dataIndex: 'total_spent',
      key: 'total_spent',
      render: (spent) => <span style={{ color: '#ff4d4f' }}>-₨{spent?.toFixed(2)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Add Balance">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAddBalance(record)}
            />
          </Tooltip>
          <Tooltip title="View Transactions">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setTransactionSearchTerm(record.user.email);
                setActiveTab('transactions');
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const transactionColumns = [
    {
      title: 'Date & Time',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (
        <div>
          <div style={{ fontWeight: '500' }}>{dayjs(date).format('MMM DD, YYYY')}</div>
          <div style={{ fontSize: '14px', color: '#666' }}>{dayjs(date).format('HH:mm:ss')}</div>
        </div>
      ),
    },
    {
      title: 'User',
      dataIndex: ['wallet', 'user'],
      key: 'user',
      render: (user) => (
        <div>
          <div style={{ fontWeight: '500' }}>{user?.name || 'N/A'}</div>
          <div style={{ fontSize: '14px', color: '#666' }}>{user?.email || 'N/A'}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag
          color={type === 'credit' ? 'green' : 'red'}
          icon={type === 'credit' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
        >
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <span
          style={{
            fontWeight: '600',
            color: record.type === 'credit' ? '#52c41a' : '#ff4d4f',
          }}
        >
          {record.type === 'credit' ? '+' : '-'}₨{amount?.toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description}>
          <span>{description}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (reference) => reference || 'N/A',
    },
    {
      title: 'Balance After',
      dataIndex: 'balance_after',
      key: 'balance_after',
      render: (balance) => <span style={{ fontWeight: '500' }}>₨{balance?.toFixed(2)}</span>,
    },
  ];

  // Calculate statistics
  const totalBalance = wallets.reduce((sum, wallet) => sum + (wallet.balance || 0), 0);
  const activeWallets = wallets.filter((w) => w.status === 'active').length;

  const totalCredits = transactions
    .filter((t) => t.type === 'credit')
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalDebits = transactions
    .filter((t) => t.type === 'debit')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <div>
      <Title level={2}>
        <WalletOutlined /> Wallet Information
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Wallets"
              value={wallets.length}
              prefix={<WalletOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Wallets"
              value={activeWallets}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Balance"
              value={totalBalance}
              prefix={<WalletOutlined />}
              precision={2}
              valueStyle={{ color: totalBalance >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={transactions.length}
              prefix={<TransactionOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent Commission Changes - Enhanced */}
      {commissionChanges.length > 0 ? (
        <Alert
          message={
            <Space>
              <TrophyOutlined style={{ color: '#52c41a' }} />
              <span style={{ fontWeight: '600' }}>
                Recent Commission Activity
                <Badge
                  count={commissionChanges.length}
                  style={{ backgroundColor: '#52c41a', marginLeft: 8 }}
                />
              </span>
              {commissionChangesLoading && (
                <span style={{ fontSize: '14px', color: '#999' }}>
                  <ReloadOutlined spin style={{ marginRight: 4 }} />
                  Updating...
                </span>
              )}
            </Space>
          }
          description={
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <span style={{ fontSize: '16px', color: '#666' }}>
                  Latest commission activities from providers
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetchCommissionChanges()}
                  loading={commissionChangesLoading}
                >
                  Refresh
                </Button>
              </div>
              <Timeline mode="left" style={{ marginTop: 16 }}>
                {commissionChanges.slice(0, 5).map((change, _index) => (
                  <Timeline.Item
                    key={change.id}
                    dot={
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: change.impact === 'positive' ? '#f6ffed' : '#fff2f0',
                          border: `2px solid ${change.impact === 'positive' ? '#52c41a' : '#ff4d4f'}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {change.impact === 'positive' ? (
                          <ArrowUpOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                        ) : (
                          <ArrowDownOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                        )}
                      </div>
                    }
                    color={change.impact === 'positive' ? '#52c41a' : '#ff4d4f'}
                  >
                    <Card
                      size="small"
                      style={{
                        marginBottom: 8,
                        border: `1px solid ${change.impact === 'positive' ? '#b7eb8f' : '#ffccc7'}`,
                        backgroundColor: change.impact === 'positive' ? '#f6ffed' : '#fff2f0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: '600',
                              marginBottom: 4,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <Tag
                              color={
                                change.user_type === 'admin'
                                  ? 'red'
                                  : change.user_type === 'provider'
                                    ? 'blue'
                                    : change.user_type === 'delivery'
                                      ? 'green'
                                      : 'orange'
                              }
                              style={{ margin: 0 }}
                            >
                              {change.user_type?.toUpperCase()}
                            </Tag>
                            <span>{change.user_name}</span>
                          </div>
                          <div style={{ color: '#666', fontSize: '16px', marginBottom: 4 }}>
                            {change.description}
                          </div>
                          {change.order_id && (
                            <div style={{ fontSize: '14px', color: '#999' }}>
                              <LinkOutlined style={{ marginRight: 4 }} />
                              Order #{change.order_id}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: 16, minWidth: 120 }}>
                          <div
                            style={{
                              fontWeight: '700',
                              fontSize: '20px',
                              color: change.impact === 'positive' ? '#52c41a' : '#ff4d4f',
                              marginBottom: 4,
                            }}
                          >
                            {change.impact === 'positive' ? '+' : ''}₨{change.amount?.toFixed(2)}
                          </div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#999',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {dayjs(change.timestamp).fromNow()}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Timeline.Item>
                ))}
              </Timeline>
              {commissionChanges.length > 5 && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button
                    type="primary"
                    ghost
                    size="small"
                    onClick={() => setActiveTab('transactions')}
                    icon={<EyeOutlined />}
                  >
                    View {commissionChanges.length - 5} More Commission Activities
                  </Button>
                </div>
              )}
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button type="link" size="small" onClick={() => setActiveTab('transactions')}>
              View All Transactions
            </Button>
          }
        />
      ) : (
        !commissionChangesLoading && (
          <Alert
            message={
              <Space>
                <TrophyOutlined style={{ color: '#d9d9d9' }} />
                <span style={{ fontWeight: '600' }}>No Recent Commission Activity</span>
              </Space>
            }
            description={
              <div style={{ marginTop: 8 }}>
                <p style={{ color: '#666', marginBottom: 16 }}>
                  No commission activities have been recorded recently. Commission activities will
                  appear here when providers earn from completed orders.
                </p>
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetchCommissionChanges()}
                  loading={commissionChangesLoading}
                >
                  Refresh
                </Button>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )
      )}

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <WalletOutlined /> Wallets ({wallets.length})
              </span>
            }
            key="wallets"
          >
            {/* Wallets Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Search users..."
                  value={walletSearchTerm}
                  onChange={(e) => setWalletSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Col>
              <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
                <Space>
                  <Button icon={<DownloadOutlined />}>Export</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleManualTransaction}>
                    Manual Transaction
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Wallets Table */}
            <Table
              columns={walletColumns}
              dataSource={wallets}
              rowKey="id"
              loading={walletsLoading}
              pagination={{
                total: wallets.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} wallets`,
              }}
              scroll={{ x: 1200 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <TransactionOutlined /> Transactions ({transactions.length})
              </span>
            }
            key="transactions"
          >
            {/* Transactions Filters */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Search transactions..."
                  value={transactionSearchTerm}
                  onChange={(e) => setTransactionSearchTerm(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by type"
                  value={transactionTypeFilter}
                  onChange={setTransactionTypeFilter}
                  allowClear
                  style={{ width: '100%' }}
                >
                  <Option value="credit">Credit</Option>
                  <Option value="debit">Debit</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Start Date', 'End Date']}
                  value={dateRange}
                  onChange={setDateRange}
                />
              </Col>
              <Col xs={24} sm={24} md={6} style={{ textAlign: 'right' }}>
                <Button icon={<DownloadOutlined />}>Export Transactions</Button>
              </Col>
            </Row>

            {/* Transaction Summary */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Total Credits"
                    value={totalCredits}
                    prefix={<ArrowDownOutlined />}
                    precision={2}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Total Debits"
                    value={totalDebits}
                    prefix={<ArrowUpOutlined />}
                    precision={2}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Net Flow"
                    value={totalCredits - totalDebits}
                    prefix={<WalletOutlined />}
                    precision={2}
                    valueStyle={{ color: totalCredits - totalDebits >= 0 ? '#52c41a' : '#ff4d4f' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Transactions Table */}
            <Table
              columns={transactionColumns}
              dataSource={transactions}
              rowKey="id"
              loading={transactionsLoading}
              pagination={{
                total: transactions.length,
                pageSize: 15,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Add Balance Modal */}
      <Modal
        title={`Add Balance - ${selectedWallet?.user?.name}`}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedWallet(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitAddBalance}>
          <Form.Item name="current_balance" label="Current Balance">
            <Input
              value={`₨${selectedWallet?.balance?.toFixed(2) || '0.00'}`}
              disabled
              style={{ fontWeight: '600' }}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount to Add"
            rules={[
              { required: true, message: 'Please enter amount' },
              { type: 'number', min: 1, message: 'Amount must be at least 1' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter amount"
              min={1}
              precision={2}
              formatter={(value) => `₨${value}`}
              parser={(value) => value.replace(/₨\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for adding balance"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setSelectedWallet(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={addBalanceMutation.isLoading}>
                Add Balance
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Manual Transaction Modal */}
      <Modal
        title="Create Manual Transaction"
        open={isTransactionModalVisible}
        onCancel={() => {
          setIsTransactionModalVisible(false);
          transactionForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={transactionForm} layout="vertical" onFinish={handleSubmitTransaction}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="user_id"
                label="User"
                rules={[{ required: true, message: 'Please select user' }]}
              >
                <Select
                  placeholder="Select user"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {wallets.map((wallet) => (
                    <Option key={wallet.user_id} value={wallet.user_id}>
                      {wallet.user?.name} ({wallet.user?.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="type"
                label="Transaction Type"
                rules={[{ required: true, message: 'Please select transaction type' }]}
              >
                <Select placeholder="Select transaction type">
                  <Option value="credit">Credit</Option>
                  <Option value="debit">Debit</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="amount"
                label="Amount"
                rules={[
                  { required: true, message: 'Please enter amount' },
                  { type: 'number', min: 0.01, message: 'Amount must be positive' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Enter amount"
                  min={0.01}
                  precision={2}
                  formatter={(value) => `₨${value}`}
                  parser={(value) => value.replace(/₨\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="reference" label="Reference (Optional)">
                <Input placeholder="Order ID, Invoice #, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter transaction description"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsTransactionModalVisible(false);
                  transactionForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={manualTransactionMutation.isLoading}
              >
                Create Transaction
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WalletInformation;
